import { appConfig, type SupportedLocale } from '@/config/app.config';
import type { AuthSession, ProjectId, ThemeMode, ToastPosition } from '@/models/model.type';
import type { User } from '@/models/model.type';

const isBrowser = typeof window !== 'undefined';

function safeGetItem(key: string): string | null {
  if (!isBrowser) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  if (!isBrowser) return;
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage quota exceeded or private browsing
  }
}

function safeRemoveItem(key: string): void {
  if (!isBrowser) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore
  }
}

export const localSource = {
  getToken(): string | null {
    return safeGetItem(appConfig.tokenKey);
  },

  setToken(token: string): void {
    safeSetItem(appConfig.tokenKey, token);
  },

  removeToken(): void {
    safeRemoveItem(appConfig.tokenKey);
  },

  getRefreshToken(): string | null {
    return safeGetItem(appConfig.refreshTokenKey);
  },

  setRefreshToken(token: string): void {
    safeSetItem(appConfig.refreshTokenKey, token);
  },

  getProject(): ProjectId | null {
    const project = safeGetItem(appConfig.projectKey);
    return project === 'aksara' || project === 'silsilah' ? project : null;
  },

  setProject(project: ProjectId): void {
    safeSetItem(appConfig.projectKey, project);
  },

  removeProject(): void {
    safeRemoveItem(appConfig.projectKey);
  },

  getSessions(): Partial<Record<ProjectId, AuthSession>> {
    const raw = safeGetItem(appConfig.sessionsKey);
    if (!raw) return {};
    try {
      const sessions = JSON.parse(raw) as Partial<Record<ProjectId, AuthSession>>;
      for (const session of Object.values(sessions)) {
        if (session?.user?.createdAt) session.user.createdAt = new Date(session.user.createdAt);
      }
      return sessions;
    } catch {
      return {};
    }
  },

  setSessions(sessions: Partial<Record<ProjectId, AuthSession>>): void {
    safeSetItem(appConfig.sessionsKey, JSON.stringify(sessions));
  },

  getUser(): User | null {
    const raw = safeGetItem(appConfig.userKey);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as User;
      return {
        ...parsed,
        createdAt: new Date(parsed.createdAt),
      };
    } catch {
      return null;
    }
  },

  setUser(user: User): void {
    safeSetItem(appConfig.userKey, JSON.stringify(user));
  },

  removeUser(): void {
    safeRemoveItem(appConfig.userKey);
  },

  clearAuth(): void {
    this.removeToken();
    safeRemoveItem(appConfig.refreshTokenKey);
    safeRemoveItem(appConfig.projectKey);
    safeRemoveItem(appConfig.sessionsKey);
    this.removeUser();
  },

  getTheme(): ThemeMode | null {
    const theme = safeGetItem(appConfig.themeKey);
    if (theme === 'light' || theme === 'dark' || theme === 'system') {
      return theme;
    }
    return null;
  },

  setTheme(theme: ThemeMode): void {
    safeSetItem(appConfig.themeKey, theme);
  },

  getLocale(): SupportedLocale | null {
    const locale = safeGetItem(appConfig.localeKey);
    if (locale === 'en' || locale === 'id') {
      return locale;
    }
    return null;
  },

  setLocale(locale: SupportedLocale): void {
    safeSetItem(appConfig.localeKey, locale);
  },

  getToastPosition(): ToastPosition | null {
    const position = safeGetItem(appConfig.toastPositionKey);
    const valid: ToastPosition[] = [
      'top-left',
      'top-center',
      'top-right',
      'center',
      'bottom-left',
      'bottom-center',
      'bottom-right',
    ];
    if (position && valid.includes(position as ToastPosition)) {
      return position as ToastPosition;
    }
    return null;
  },

  setToastPosition(position: ToastPosition): void {
    safeSetItem(appConfig.toastPositionKey, position);
  },
};
